import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    FactCheck as AttendanceIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader, { capitalizeFirst } from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintAttendanceSheetPage: React.FC = () => {
    const { language, t } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const todayStr = new Date().toISOString().slice(0, 10);
    const currentMonthStr = todayStr.slice(0, 7);

    const [selectedGroupId, setSelectedGroupId] = useState<number | 'all'>('all');
    const [numColumns, setNumColumns] = useState<number>(10);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthStr);

    const printableRef = useRef<HTMLDivElement>(null);

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    // Map student to their group
    const studentGroupMap = useMemo(() => {
        const map = new Map<number, string>();
        groupStudents.forEach(gs => {
            if (gs.status !== 'Dropped') {
                const grp = groups.find(g => g.id === gs.group_id);
                if (grp) map.set(gs.student_id, grp.name);
            }
        });
        return map;
    }, [groups, groupStudents]);

    // Filter students by selected group
    const filteredStudents = useMemo(() => {
        if (selectedGroupId === 'all') {
            return [...students].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
        }
        const enrolledStudentIds = new Set(
            groupStudents
                .filter(gs => gs.group_id === selectedGroupId && gs.status !== 'Dropped')
                .map(gs => gs.student_id)
        );
        return students
            .filter(s => enrolledStudentIds.has(s.id))
            .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }, [students, selectedGroupId, groupStudents]);

    const selectedGroupObj = groups.find(g => g.id === selectedGroupId);
    const groupLabel = selectedGroupId === 'all'
        ? (language === 'ar' ? 'جميع الأقسام' : language === 'en' ? 'All classes' : 'Toutes les classes')
        : (selectedGroupObj?.name || '');

    // Formatted Month/Year
    const monthDate = new Date(`${selectedMonth}-01T00:00:00`);
    const formattedMonth = capitalizeFirst(
        monthDate.toLocaleDateString(language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR', {
            month: 'long',
            year: 'numeric'
        })
    );
    const academicYear = `${monthDate.getFullYear()}-${monthDate.getFullYear() + 1}`;

    // Document Title
    const docTitle = language === 'ar'
        ? `ورقة الحضور — ${groupLabel}`
        : language === 'en'
        ? `Attendance Sheet — ${groupLabel}`
        : `Feuille de présence — ${groupLabel}`;

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '5px 6px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const dayCols = Array.from({ length: Math.max(1, Math.min(numColumns, 31)) }, (_, i) => i + 1);

    // Export to Excel
    const handleExportExcel = () => {
        const headers = [
            'N°',
            language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom',
            language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe',
            ...dayCols.map(d => (language === 'ar' ? `ي ${d}` : `J ${d}`)),
            language === 'ar' ? 'ملاحظة' : language === 'en' ? 'Observations' : 'Observations'
        ];

        const rows = filteredStudents.map((s, idx) => {
            const grp = studentGroupMap.get(s.id) || s.school_level || (language === 'ar' ? 'بدون قسم' : 'Sans classe');
            return [
                idx + 1,
                `${s.last_name || ''} ${s.first_name || ''}`.trim(),
                grp,
                ...dayCols.map(() => ''),
                ''
            ];
        });

        exportToExcel(`Presence_${groupLabel.replace(/\s+/g, '_')}_${selectedMonth}`, 'Presence', headers, rows);
    };

    // Export to Word
    const handleExportWord = () => {
        if (!printableRef.current) return;
        exportToWord(
            `Presence_${groupLabel.replace(/\s+/g, '_')}_${selectedMonth}`,
            docTitle,
            printableRef.current.innerHTML,
            isRtl
        );
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                    value={selectedGroupId}
                    onChange={e => setSelectedGroupId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                >
                    <MenuItem value="all">
                        {language === 'ar' ? 'جميع الأقسام' : language === 'en' ? 'All classes' : 'Toutes les classes'}
                    </MenuItem>
                    {groups.map(g => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    type="number"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الأعمدة (أيام)' : language === 'en' ? 'Columns (days)' : 'Colonnes (jours)'}
                    value={numColumns}
                    inputProps={{ min: 1, max: 31 }}
                    onChange={e => setNumColumns(Math.max(1, Math.min(31, Number(e.target.value) || 10)))}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    type="month"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الشهر' : language === 'en' ? 'Month' : 'Mois'}
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'ورقة الحضور / قائمة القسم' : language === 'en' ? 'Attendance Sheet' : 'Feuille de présence / Liste de classe'}
            icon={<AttendanceIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={docTitle}
                    subtitle={`${language === 'ar' ? 'الشهر' : language === 'en' ? 'Month' : 'Mois'} : ${formattedMonth} — ${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, fontWeight: 600 }}>
                    <div>
                        {filteredStudents.length} {language === 'ar' ? 'تلميذ' : language === 'en' ? 'student(s)' : 'apprenant(s)'}
                    </div>
                    <div>
                        {todayStr}
                    </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                            <th style={cellStyle(35, { textAlign: 'center' })}>N°</th>
                            <th style={cellStyle(180)}>
                                {language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom'}
                            </th>
                            <th style={cellStyle(100)}>
                                {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                            </th>
                            {dayCols.map(d => (
                                <th key={d} style={cellStyle(28, { textAlign: 'center', fontSize: 10 })}>
                                    {language === 'ar' ? `ي ${d}` : `J ${d}`}
                                </th>
                            ))}
                            <th style={cellStyle(70, { textAlign: 'center' })}>
                                {language === 'ar' ? 'ملاحظة' : language === 'en' ? 'Obs.' : 'Obs.'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudents.length === 0 ? (
                            <tr>
                                <td colSpan={4 + dayCols.length} style={{ ...cellStyle(), textAlign: 'center', padding: '20px 10px', color: '#777' }}>
                                    {language === 'ar' ? 'لا يوجد تلاميذ مسجلين' : language === 'en' ? 'No students enrolled' : 'Aucun apprenant inscrit'}
                                </td>
                            </tr>
                        ) : (
                            filteredStudents.map((s, idx) => {
                                const grp = studentGroupMap.get(s.id) || s.school_level || (language === 'ar' ? 'بدون قسم' : 'Sans classe');
                                return (
                                    <tr key={s.id}>
                                        <td style={cellStyle(35, { textAlign: 'center', fontWeight: 'bold' })}>{idx + 1}</td>
                                        <td style={cellStyle(180, { fontWeight: 600 })}>
                                            {s.last_name} {s.first_name}
                                        </td>
                                        <td style={cellStyle(100)}>{grp}</td>
                                        {dayCols.map(d => (
                                            <td key={d} style={cellStyle(28, { textAlign: 'center', color: '#ccc' })}>—</td>
                                        ))}
                                        <td style={cellStyle(70)} />
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* Footer notes & signatures */}
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#555' }}>
                    <div>
                        {filteredStudents.length} {language === 'ar' ? 'سطر — شكراً على ثقتكم.' : language === 'en' ? 'line(s) — Thank you for your trust.' : 'ligne(s) — Merci de votre confiance.'}
                    </div>
                </div>

                <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left', verticalAlign: 'top' }}>
                                <strong>{language === 'ar' ? 'المعلم(ة) :' : language === 'en' ? 'Teacher :' : "L'enseignant(e) :"}</strong>
                                <div style={{ height: 60 }} />
                            </td>
                            <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right', verticalAlign: 'top' }}>
                                <strong>{language === 'ar' ? 'المدير :' : language === 'en' ? 'Director :' : 'Le directeur :'}</strong>
                                <div style={{ height: 60 }} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </GenericPage>
    );
};

export default PrintAttendanceSheetPage;
